import { useRef } from 'react';

import { cn } from '@rocco/utils/cn';
import { usePageCtas } from '~/modules/enquiry/component/PageCtasProvider';
import {
    useBookCta,
    useEnquireCta,
} from '~/modules/enquiry/hooks/use-page-ctas';
import { DIRECTORY_GUTTER_X } from '~/modules/articles-revamp/component/EditorialArticleGrid';
import { detailCta, SectionHead } from '~/ui-rocco/pages/detail-shared';
import {
    ACTION_CARD_ACTION_CLASS,
    ACTION_CARD_CLASS,
    ACTION_CARD_CONTACT_IMAGE,
    ACTION_CARD_WEBSITE_IMAGE,
    ACTION_CARDS_GRID_CLASS,
    ActionCardBody,
    ContactPeek,
} from '~/ui-rocco/pages/detail-shared/DetailActionCards';
import {
    ModalPeek,
    useGrowToModal,
} from '~/ui-rocco/pages/detail-shared/ModalPeek';
import { BookMeetingMiniModal } from '~/ui-rocco/pages/detail-shared/BookMeetingPeek';
import { EnquiryMiniModal } from '~/ui-rocco/pages/detail-shared/EnquiryPeek';
import { ARTICLE_GROUND } from '../util/article-ground';
import type { RevampProfessional } from '../api/get-article-revamp-data.server';

const BOOK_A_MEETING = 'Book a Meeting';
const SEND_AN_ENQUIRY = 'Send an enquiry';

interface ArticleHireSectionProps {
    professional: RevampProfessional;
    articleTitle: string;
    /** The article's own pictures, grounding the two cards: the banner first. */
    images: string[];
}

/**
 * The project page's two-card offer, at the foot of the article: book a
 * meeting with the practice the article is about, or send them an enquiry —
 * each a photograph with a sentence saying what the reader gets for taking
 * it, and the whole card the control. Each card's hover is the modal it
 * opens, small, and the click grows it into the real one.
 *
 * Both cards read the article's page CTAs, so it must sit inside that
 * provider. Book a Meeting opens the one booking gate every entry point
 * opens; with the gate off it falls back to the enquiry modal. While the flat
 * settings decide, the Book card shows whenever the enquiry does; once the
 * professional assigns Book a meeting to article pages, that assignment
 * decides.
 */
export const ArticleHireSection = ({
    professional,
    articleTitle,
    images,
}: ArticleHireSectionProps) => {
    const { ctas } = usePageCtas();
    const enquire = useEnquireCta({ fixedLabel: SEND_AN_ENQUIRY });
    const book = useBookCta({ fixedLabel: BOOK_A_MEETING });
    const heroImage = images[0];

    const practice = professional.title;
    const meetingCardImage = images[0] ?? ACTION_CARD_WEBSITE_IMAGE;
    const enquiryCardImage =
        images[1] ?? images[0] ?? ACTION_CARD_CONTACT_IMAGE;

    const meetingPeekRef = useRef<HTMLDivElement>(null);
    const meetingMini = (
        <BookMeetingMiniModal
            subject={{
                image: heroImage,
                practice,
                companyLogo: professional.logo,
                logoBackgroundColor: professional.logoBackground,
            }}
        />
    );
    const meetingFlight = useGrowToModal(meetingPeekRef, meetingMini);
    const showBook = ctas.book.usesForms ? book.visible : enquire.visible;
    const canBook = ctas.book.viaGate && !!professional.urlSegment;
    const onBookMeeting = () => {
        if (canBook) {
            meetingFlight.grow(book.open);
        } else if (ctas.book.viaGate) {
            // The gate is keyed on the profile's slug; without one the
            // enquiry modal stands in, as it does with the gate off.
            enquire.open({ title: book.label, ctaLabel: book.label });
        } else {
            book.open();
        }
    };

    const enquiryPeekRef = useRef<HTMLDivElement>(null);
    const enquiryMini = (
        <EnquiryMiniModal
            subject={{
                image: heroImage,
                practice,
                note: professional.customEnquiryMessage,
                itemType: 'article',
            }}
        />
    );
    const enquiryFlight = useGrowToModal(enquiryPeekRef, enquiryMini);
    const onSendEnquiry = () => enquiryFlight.grow(() => enquire.open());

    if (!showBook && !enquire.visible) return null;

    return (
        <section
            className={cn(
                'w-full max-md:pt-[3rem] md:pt-[7.5rem]',
                ARTICLE_GROUND,
                DIRECTORY_GUTTER_X,
                // The article's one phone gutter is the prose's 18px, over
                // the directory's 16.
                'px-4.5',
            )}
        >
            <SectionHead
                voice="editorial"
                lead="Like this Article?"
                subject={`Get in touch with ${practice}`}
                subjectLink={professional.link}
            />

            <div className={ACTION_CARDS_GRID_CLASS}>
                {showBook ? (
                    <button
                        type="button"
                        onClick={onBookMeeting}
                        className={ACTION_CARD_CLASS}
                    >
                        <ActionCardBody
                            image={meetingCardImage}
                            hoverTint
                            peek={
                                canBook ? (
                                    <ModalPeek boxRef={meetingPeekRef}>
                                        {meetingMini}
                                    </ModalPeek>
                                ) : (
                                    <ContactPeek
                                        image={heroImage}
                                        title={practice}
                                        subtitle={articleTitle}
                                    />
                                )
                            }
                            copy={
                                <>
                                    Book a meeting with {practice} to talk
                                    through your own project, their approach and
                                    how they could take it on.
                                </>
                            }
                            action={
                                <span
                                    className={cn(
                                        detailCta('white-lifted'),
                                        ACTION_CARD_ACTION_CLASS,
                                    )}
                                >
                                    {book.label}
                                </span>
                            }
                        />
                    </button>
                ) : null}

                {enquire.visible ? (
                    <button
                        type="button"
                        onClick={onSendEnquiry}
                        className={ACTION_CARD_CLASS}
                    >
                        <ActionCardBody
                            image={enquiryCardImage}
                            hoverTint
                            peek={
                                <ModalPeek boxRef={enquiryPeekRef}>
                                    {enquiryMini}
                                </ModalPeek>
                            }
                            copy={
                                <>
                                    Send {practice} an enquiry about your
                                    project, their availability and fees, or
                                    anything you would like to know first.
                                </>
                            }
                            action={
                                <span
                                    className={cn(
                                        detailCta('white-lifted'),
                                        ACTION_CARD_ACTION_CLASS,
                                    )}
                                >
                                    {enquire.label}
                                </span>
                            }
                        />
                    </button>
                ) : null}
            </div>
            {meetingFlight.element}
            {enquiryFlight.element}
        </section>
    );
};
